import { createContext, ReactNode, useContext, useMemo } from "react";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";

type TConfiguratorTranslations = {
  configuratorPageT: ReturnType<typeof useConfiguratorPageT>;
  genericTranslationsT: ReturnType<typeof useGenericTranslationsT>;
};

const ConfiguratorTranslationsContext = createContext<TConfiguratorTranslations | null>(null);

// useConfiguratorPageT and useGenericTranslationsT each resolve a fresh object of ~80 and ~50 strings,
// memoized per component instance rather than shared. The configurator's list rows mount and unmount
// as the user scrolls, so every row was paying for both. Resolving them once per page and handing them
// down through context keeps a row's mount free of i18next lookups.
export const ConfiguratorTranslationsProvider = ({ children }: { children: ReactNode }) => {
  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const value = useMemo(() => ({ configuratorPageT, genericTranslationsT }), [configuratorPageT, genericTranslationsT]);

  return <ConfiguratorTranslationsContext.Provider value={value}>{children}</ConfiguratorTranslationsContext.Provider>;
};

export const useConfiguratorTranslations = (): TConfiguratorTranslations => {
  const translations = useContext(ConfiguratorTranslationsContext);
  if (!translations) throw new Error("useConfiguratorTranslations must be called inside a ConfiguratorTranslationsProvider");
  return translations;
};
